import { createStore, applyMiddleware } from "redux"
import thunk from "redux-thunk"
import { composeWithDevTools } from "redux-devtools-extension"
import logger from "redux-logger"

import rootReducer from "./rootReducer"

// actions
import * as preferencesAction from "./preference/actions"
import * as userAction from "./user/actions"
import * as modalAction from "./modal/actions"
import * as sheetAction from "./sheet/actions"
import * as productAction from "./product/actions"
import * as campaignAction from "./campaign/actions"
import * as postAction from "./post/actions"
import * as indicatorAction from "./indicator/actions"
import * as authAction from "./auth/actions"
import * as sidebarAction from "./sidebar/actions"
import * as notificationAction from "./notification/actions"
import * as brandAction from "./brand/actions"
import * as popupAction from "./popup/actions"
import * as settingsAction from "./settings/actions"
import * as pathCachesAction from "./pathCaches/actions"
import * as instagramAction from "./instagram/actions"
import * as appealReelsAction from "./appealReels/actions"

import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"
import { isEmpty } from "lodash"

// const middleWare = applyMiddleware(logger, thunk);
const middleWare = applyMiddleware(thunk)
const persistConfig = {
  key: "root",
  storage: storage,
  whitelist: ["auth", "preferences", "instagram", "appealReels"],
}

const enhancedReducer = persistReducer(persistConfig, rootReducer)

export const searchCampaignSelectedStep = (state) => {
  return state.campaign.steps.filter((step) => step.selected)[0]?.value ?? 1
}

export const mapStateToProps = (state) => {
  return {
    preferences: state.preferences.preferences,
    user: state.user.user,
    auth: state.auth.auth,
    modal: state.modal.modal,
    sheet: state.sheet.sheet,
    popup: state.popup.popup,
    campaignSelectedStep: searchCampaignSelectedStep(state),
    campaignSteps: state.campaign.steps,
    campaign: state.campaign.campaign,
    campaignRules: state.campaign.rules,
    campaignBaseRules: state.campaign.baseRules,
    product: state.product.product,
    post: state.post.post,
    indicator: state.indicator.indicator,
    sidebar: state.sidebar.sidebar,
    notification: state.notification.notification,
    brand: state.brand.brand,
    settings: state.settings.settings,
    pathCaches: state.pathCaches.pathCaches,
    instagram: state.instagram.instagram,
    appealReels: state.appealReels.appealReels
  }
}

export const mapDispatchProps = (dispatch) => {
  return {
    // Preferences
    setPreferences: (payload) => {
      dispatch(preferencesAction.setPreferences(payload))
    },

    // User
    setUser: (payload) => {
      dispatch(userAction.setUser(payload))
    },

    // Auth
    setAuth: (payload) => {
      dispatch(authAction.setAuth(payload))
    },

    // Brand
    setBrand: (payload) => {
      dispatch(brandAction.setBrand(payload))
    },

    // Logout
    logout: () => {
      dispatch(brandAction.logout())
      dispatch(userAction.logout())
      dispatch(authAction.logout())
      dispatch(sheetAction.setSheet({}))
      dispatch(modalAction.setModal({}))
      dispatch(popupAction.setPopup({}))
      // dispatch(pathCachesAction.clearPathCaches())
    },

    // Modal
    setModal: (payload) => {
      dispatch(modalAction.setModal(payload))
    },

    // Show Modal
    showModal: (payload) => {
      dispatch(
        modalAction.setModal({
          ...payload,
          isVisible: true,
          closeModal: false,
        })
      )
    },

    // Close Modal
    closeModal: () => {
      dispatch(modalAction.closeModal())
    },

    // Sheet
    setSheet: (payload) => {
      dispatch(sheetAction.setSheet(payload))
    },

    // Show Sheet
    showSheet: (payload) => {
      dispatch(sheetAction.setSheet({ ...payload, ...{ isVisible: true } }))
    },

    // Close Sheet
    closeSheet: () => {
      dispatch(sheetAction.closeSheet())
    },

    // Popup
    setPopup: (payload) => {
      dispatch(popupAction.setPopup(payload))
    },

    // Show Popup
    showPopup: (payload) => {
      dispatch(popupAction.setPopup({ ...payload, ...{ isVisible: true } }))
    },

    // Close Popup
    closePopup: () => {
      dispatch(popupAction.closePopup())
    },

    // Product
    setProduct: (payload) => {
      dispatch(productAction.setProduct(payload))
    },

    // CampaignSteps
    setCampaignSteps: (payload) => {
      dispatch(campaignAction.setCampaignSteps(payload))
    },

    // Campaign
    setCampaign: (payload) => {
      dispatch(campaignAction.setCampaign(payload))
    },

    // CampaignRules
    setCampaignRules: (payload) => {
      dispatch(campaignAction.setCampaignRules(payload))
    },

    // CampaignBaseRules
    setCampaignBaseRules: (payload) => {
      dispatch(campaignAction.setCampaignBaseRules(payload))
    },

    resetCampaignRules: () => {
      dispatch(campaignAction.resetCampaignRules())
    },

    resetCampaignState: (payload) => {
      dispatch(campaignAction.resetCampaignState(payload))
    },

    // Post
    setPost: (payload) => {
      dispatch(postAction.setPost(payload))
    },

    // Indicator
    setIndicator: (payload) => {
      dispatch(indicatorAction.setIndicator(payload))
    },

    // Sidebar
    setSidebar: (payload) => {
      dispatch(sidebarAction.setSidebar(payload))
    },

    // Notification
    setNotification: (payload) => {
      dispatch(notificationAction.setNotification(payload))
    },

    // Show Notification
    showNotification: (payload) => {
      dispatch(
        notificationAction.setNotification({
          ...payload,
          ...{ isVisible: true },
        })
      )
    },

    showOverlays: (payload) => {
      if (store.getState().preferences.preferences.isMobile) {
        dispatch(sheetAction.setSheet({ ...payload, ...{ isVisible: true } }))
      } else {
        dispatch(popupAction.setPopup({ ...payload, ...{ isVisible: true } }))
      }
    },

    closeOverlays: () => {
      if (store.getState().preferences.preferences.isMobile) {
        if (!isEmpty(store.getState().sheet.sheet)) {
          dispatch(sheetAction.closeSheet())
        }
      } else {
        if (!isEmpty(store.getState().popup.popup)) {
          dispatch(popupAction.closePopup())
        }
      }
    },

    setSettings: (payload) => {
      dispatch(settingsAction.setSettings(payload))
    },

    setInstagram: (payload) => {
      dispatch(instagramAction.setInstagram(payload))
    },

    setAppealReels: (payload) => {
      dispatch(appealReelsAction.setAppealReels(payload))
    },

    addPathCache: (payload) => {
      dispatch(pathCachesAction.addPathCache(payload))
    },

    removePathCache: (payload) => {
      dispatch(pathCachesAction.removePathCache(payload))
    },

    clearPathCaches: (payload) => {
      dispatch(pathCachesAction.clearPathCaches(payload))
    },
  }
}

const store = createStore(enhancedReducer, composeWithDevTools(middleWare))
export default store
